import { useState } from "react";
import { Users, ChevronRight, Settings, LogOut, Megaphone, X } from "lucide-react";
import { cn } from "@vc/lib/utils";
import { useTranslation } from "@/clientWeb/i18n/LanguageContext";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/clientWeb/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/clientWeb/components/ui/dropdown-menu";
import { BroadcastMessageDialog } from "../breakout/BroadcastMessageDialog";
import { useBreakoutBroadcastStore } from "@/features/meeting/store/breakout-broadcast.store";
import type { BreakoutRoom } from "@vc/viewmodels/breakout/useBreakoutRooms";

interface BreakoutRoomsControlProps {
  activeBreakoutRooms: BreakoutRoom[];
  currentBreakoutRoomId?: string | null;
  onJoinRoom?: (roomId: string) => void;
  onLeaveRoom?: () => void;
  onOpenManage?: () => void;
  /**
   * The viewer may open and run breakout rooms.
   *
   * Without this the control only appeared once rooms already existed, which
   * left a moderator no way to create any: the button they needed was hidden
   * until after the thing it creates had been created.
   */
  canManage?: boolean;
  /**
   * The room has actually started them — not a moderator still laying rooms
   * out. Once started, the way to CREATE rooms goes away: they exist, and the
   * only things left to do are move between them and leave.
   */
  started?: boolean;
  /**
   * The room this participant has been put in, if any. Where they belong, which
   * is not the same as where they are — that is `currentBreakoutRoomId`.
   */
  assignedRoomId?: string | null;
  /** Close every room and bring everybody back. Moderators only. */
  onEndRooms?: () => void;
  onBroadcastMessage?: (message: string, roomIds: string[], id: string) => void;
  compact?: boolean;
}

export function BreakoutRoomsControl({
  activeBreakoutRooms,
  currentBreakoutRoomId,
  onJoinRoom,
  onLeaveRoom,
  onOpenManage,
  canManage = false,
  started = false,
  assignedRoomId = null,
  onEndRooms,
  onBroadcastMessage,
  compact = false,
}: BreakoutRoomsControlProps) {
  const t = useTranslation();
  const [isJoining, setIsJoining] = useState<string | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [broadcastDialogOpen, setBroadcastDialogOpen] = useState(false);
  const unreadBroadcasts = useBreakoutBroadcastStore(
    (s) => Math.max(0, s.messages.length - s.readCount),
  );

  // Hidden only from someone with nothing to do here: no rooms open, and no
  // right to open any. A moderator always keeps the way in.
  const hasRooms = !!activeBreakoutRooms && activeBreakoutRooms.length > 0;
  if (!hasRooms && !canManage) {
    return null;
  }

  const currentRoom = activeBreakoutRooms.find((r) => r.id === currentBreakoutRoomId);
  const isInMainRoom = !currentBreakoutRoomId;
  const assignedRoom = assignedRoomId
    ? activeBreakoutRooms.find((r) => r.id === assignedRoomId)
    : undefined;

  const handleJoinRoom = (roomId: string) => {
    if (roomId === currentBreakoutRoomId) {
      setDropdownOpen(false);
      return;
    }

    setIsJoining(roomId);
    setDropdownOpen(false);

    // Simulate joining delay for animation
    setTimeout(() => {
      onJoinRoom?.(roomId);
      setIsJoining(null);
    }, 1500);
  };

  const handleLeaveRoom = () => {
    if (isInMainRoom) {
      setDropdownOpen(false);
      return;
    }

    setIsJoining("leaving");
    setDropdownOpen(false);

    setTimeout(() => {
      onLeaveRoom?.();
      setIsJoining(null);
    }, 1000);
  };

  return (
    <>
      <div className="relative">
        <div
          className={cn(
            "group flex items-center transition-all duration-300 ease-out hover:scale-105 hover:-translate-y-1 active:scale-110 overflow-hidden rounded-full bg-primary-100/40 ring-2 ring-primary"
          )}
        >
          <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
            <TooltipProvider delayDuration={100}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className={cn(
                        "relative flex items-center justify-center transition-all duration-200 cursor-pointer",
                        compact ? "h-11 w-11" : "h-10 w-10 text-white hover:bg-primary-100/20"
                      )}
                      aria-label={t("controlBar.breakoutRooms")}
                    >
                      <div className="relative">
                        {/* <Users className={cn(compact ? "h-4 w-4 text-primary" : "h-5 w-5")} /> */}
                        <svg
                          className={cn(compact ? "h-4 w-4 text-white" : "h-5 w-5 text-white")}
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          {/* main room */}
                          <circle cx="12" cy="6" r="3" />

                          {/* breakout rooms */}
                          <circle cx="6" cy="18" r="2.5" />
                          <circle cx="18" cy="18" r="2.5" />

                          {/* connections */}
                          <line x1="12" y1="9" x2="6" y2="15" />
                          <line x1="12" y1="9" x2="18" y2="15" />
                        </svg>
                        {/* Animated indicator when breakout rooms are active */}
                        {activeBreakoutRooms.length > 0 && (
                          <span className="absolute -top-1 -right-1 flex h-2.5 w-2.5">
                            <span className={cn(
                              "animate-ping absolute inline-flex h-full w-full rounded-full opacity-75",
                              currentBreakoutRoomId ? "bg-red-500" : "bg-red-400"
                            )}></span>
                            <span className={cn(
                              "relative inline-flex rounded-full h-2.5 w-2.5",
                              currentBreakoutRoomId ? "bg-red-500" : "bg-red-500"
                            )}></span>
                          </span>
                        )}
                      </div>
                    </button>
                  </DropdownMenuTrigger>
                </TooltipTrigger>
                <TooltipContent side="top" sideOffset={8} className="border-none capitalize">
                  {currentRoom ? `${t("controlBar.in")} ${currentRoom.name}` : t("controlBar.breakoutRooms")}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <DropdownMenuContent align="center" className="min-w-[200px]">
              {/* Current Room Indicator */}
              <div className="px-3 py-2 text-xs font-medium text-slate-500 dark:text-muted-foreground border-b border-stroke-100 dark:border-border mb-1">
                {currentRoom ? `${t("controlBar.currentlyIn")} ${currentRoom.name}` : t("controlBar.inMainRoom")}
              </div>

              {/* Join my room — the participant's way in.
                  Only for somebody the host has put in a room who is not in it
                  yet. A participant does not get the room list: they were
                  assigned one, and picking a different one is the moderator's
                  call (or self walk-in, which assigns them first). */}
              {!canManage && started && isInMainRoom && assignedRoom && (
                <DropdownMenuItem
                  className="cursor-pointer font-medium text-primary focus:text-primary"
                  onClick={() => handleJoinRoom(assignedRoom.id)}
                >
                  <ChevronRight className="mr-2 h-4 w-4" />
                  <span>
                    {t("controlBar.joinRoom")} · {assignedRoom.name}
                  </span>
                </DropdownMenuItem>
              )}

              {/* Switch Room — moving freely between rooms is a moderator's
                  privilege. A participant moves via their assignment, or by
                  leaving back to the main room. */}
              {canManage && (
              <DropdownMenuSub>
                <DropdownMenuSubTrigger className="cursor-pointer">
                  <ChevronRight className="mr-2 h-4 w-4" />
                  <span>{t("controlBar.switchRoom")}</span>
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="min-w-[160px]">
                  {/* Main Room Option */}
                  <DropdownMenuItem
                    className={cn(
                      "cursor-pointer",
                      isInMainRoom && "bg-slate-100 dark:bg-muted font-medium"
                    )}
                    onClick={() => handleLeaveRoom()}
                    disabled={isInMainRoom}
                  >
                    <span className="flex-1">{t("controlBar.mainRoom")}</span>
                    <span className="ml-2 text-xs text-slate-400 dark:text-muted-foreground">{t("controlBar.all")}</span>
                    {isInMainRoom && (
                      <span className="ml-2 h-2 w-2 rounded-full bg-green-500"></span>
                    )}
                  </DropdownMenuItem>

                  {/* All Breakout Rooms */}
                  {activeBreakoutRooms.map((room) => (
                    <DropdownMenuItem
                      key={room.id}
                      className={cn(
                        "cursor-pointer",
                        currentBreakoutRoomId === room.id && "bg-blue-50 dark:bg-blue-950/40 font-medium text-blue-600 dark:text-blue-400"
                      )}
                      onClick={() => handleJoinRoom(room.id)}
                      disabled={currentBreakoutRoomId === room.id}
                    >
                      <span className="flex-1">{room.name}</span>
                      <span className="ml-2 text-xs text-slate-400 dark:text-muted-foreground">
                        {room.participants?.length || 0}
                      </span>
                      {currentBreakoutRoomId === room.id && (
                        <span className="ml-2 h-2 w-2 rounded-full bg-blue-500"></span>
                      )}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
              )}

              {/* Manage — setting the rooms up.
                  Moderators only: it opens the configuration, and it used to
                  appear for everybody because it was gated on the callback
                  being passed rather than on who was looking.
                  Gone once the rooms are running: there is nothing left to
                  configure, and offering "start" again is offering to redo
                  something already done. */}
              {/* Manage stays available from ANY room, including inside a
                  breakout — running the rooms is not something a moderator
                  stops being able to do by walking into one. It no longer
                  offers Start once they are open: the dialog shows End
                  instead, which is what makes keeping it here safe. */}
              {onOpenManage && canManage && (
                <DropdownMenuItem
                  className="cursor-pointer"
                  onClick={() => {
                    setDropdownOpen(false);
                    onOpenManage();
                  }}
                >
                  <Settings className="mr-2 h-4 w-4" />
                  <span>{t("controlBar.manage")}</span>
                </DropdownMenuItem>
              )}

              {/* Broadcast Message — sending word to every room at once is a
                  moderator's job wherever they happen to be standing. It was
                  gated on being in the main room, so a moderator who walked
                  into a breakout lost the one control whose whole purpose is
                  reaching the rooms they are not in. */}
              {canManage && onBroadcastMessage && (
                <DropdownMenuItem
                  className="cursor-pointer text-amber-600 dark:text-amber-400 focus:text-amber-600 dark:focus:text-amber-400 focus:bg-amber-50 dark:focus:bg-amber-950/40"
                  onClick={() => {
                    setDropdownOpen(false);
                    setBroadcastDialogOpen(true);
                  }}
                >
                  <Megaphone className="mr-2 h-4 w-4" />
                  <span className="flex-1">{t("controlBar.broadcastMessage")}</span>
                  {/* A co-host can broadcast too, so a host has messages to be
                      told about. Without this the only way to find out was to
                      open the thread on the off-chance. */}
                  {unreadBroadcasts > 0 && (
                    <span className="ml-2 grid h-4 min-w-4 place-items-center rounded-full bg-amber-500 px-1 text-[10px] font-semibold text-white">
                      {unreadBroadcasts > 9 ? "9+" : unreadBroadcasts}
                    </span>
                  )}
                </DropdownMenuItem>
              )}

              <DropdownMenuSeparator />

              {/* Leave Room Option - Only show when in a breakout room */}
              {!isInMainRoom && (
                <DropdownMenuItem
                  className="cursor-pointer text-red-600 dark:text-red-400 focus:text-red-600 dark:focus:text-red-400 focus:bg-red-50 dark:focus:bg-red-950/40"
                  onClick={handleLeaveRoom}
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>{t("controlBar.leaveRoom")}</span>
                </DropdownMenuItem>
              )}

              {/* End the rooms. Moderators only, and only once there are rooms
                  running to end. Destructive and it lands on everybody — every
                  person in a breakout is pulled back — so it is last, behind a
                  separator, away from the movement items.

                  This used to be nested INSIDE the Switch Room submenu, between
                  "Main Room" and the room list, where it read as somewhere to
                  go rather than something to do to everyone. */}
              {canManage && started && onEndRooms && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="cursor-pointer text-destructive focus:text-destructive focus:bg-destructive/10"
                    onClick={() => {
                      setDropdownOpen(false);
                      onEndRooms();
                    }}
                  >
                    <X className="mr-2 h-4 w-4" />
                    <span>{t("controlBar.endBreakoutRooms")}</span>
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Joining Loader Overlay */}
      {isJoining && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="flex flex-col items-center gap-4 p-8 rounded-2xl bg-[#1a1a1a]/90 shadow-2xl animate-in zoom-in-95 duration-300">
            <div className="relative">
              {/* Animated circles */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="h-20 w-20 rounded-full border-4 border-primary/20 animate-ping"></div>
              </div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="h-16 w-16 rounded-full border-4 border-t-primary-500 border-r-primary-400 border-b-primary-300 border-l-transparent animate-spin"></div>
              </div>
              <div className="relative flex h-14 w-14 items-center justify-center rounded-full bg-primary shadow-lg">
                <Users className="h-7 w-7 text-white" />
              </div>
            </div>
            <div className="text-center">
              <p className="text-lg font-medium text-white">
                {isJoining === "leaving"
                  ? t("controlBar.returningToMainRoom")
                  : `${t("controlBar.joiningRoom")} ${activeBreakoutRooms.find(r => r.id === isJoining)?.name || t("controlBar.room")}...`}
              </p>
              <p className="mt-1 text-sm text-white/60">
                {t("controlBar.pleaseWaitConnect")}
              </p>
            </div>
          </div>
        </div>
      )}
      {/* Broadcast Message Dialog */}
      <BroadcastMessageDialog
        open={broadcastDialogOpen}
        onOpenChange={setBroadcastDialogOpen}
        rooms={activeBreakoutRooms}
        senderName={t("controlBar.host")}
        onSend={(message: string, roomIds: string[], id: string) => {
          onBroadcastMessage?.(message, roomIds, id);
        }}
      />
    </>
  );
}
